import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Pagination } from '@core/entities/_extra/paginated.entity';
import { TranslateModule } from '@ngx-translate/core';
import { isMobileView } from '@core/utils';
import { IconComponent } from '@shared/components/ui';

@Component({
  selector: 'app-pagination-bar',
  imports: [CommonModule, IconComponent, TranslateModule],
  templateUrl: './pagination-bar.component.html',
  styleUrls: ['./pagination-bar.component.scss'],
})
/**
 * The PaginationBarComponent is a standalone component that displays a pagination bar with page
 * numbers, page size selector, and previous and next buttons.
 *
 * NEW: Added isFrontendPagination, fullDataArray, and paginatedData properties to support frontend pagination.
 * @property {boolean} isFrontendPagination - Whether to use frontend pagination or server-side pagination.
 * @property {any[]} fullDataArray - The full data array to be paginated.
 * @property {EventEmitter<any[]>} paginatedData - The paginated data array to be emitted.
 *
 *
 * @property {Pagination} pagination - The pagination object that contains the page, pageSize,
 * rowCount and pageCount properties.
 * @property {boolean} showPageSizeSelector - Whether to show the page size selector dropdown.
 * @property {boolean} showPagination - Whether to show the page size selector dropdown.
 * @property {number[]} itemsPerPageOptions - The page size options to show in the page size selector
 * dropdown.
 * @property {boolean} showFirstLastButtons - Whether to show the first and last page buttons.
 * @property {boolean} showPreviousNextButtons - Whether to show the previous and next page buttons.
 * @property {boolean} showEllipsis - Whether to show the ellipsis (...) if page number exceeds 6.
 * @property {number} defaultItemsPerPage - The default page size to use if the pagination object does
 * not have a pageSize property.
 * @property {string} selectedPageBgColor - The background color of the selected page buttons.
 * @property {string} selectedPageTextColor - The text color of the selected page buttons.
 * @property {boolean} loading - If true, the pagination bar will be disabled.
 * @property {EventEmitter<number>} pageChanged - Emits the new page number when the page is changed.
 * @property {EventEmitter<number>} pageSizeChanged - Emits the new page size when the page size is
 * changed.
 *
 * @example
 * <app-pagination-bar [pagination]="pagination"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [showPageSizeSelector]="false"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [showPagination]="false"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [showFirstLastButtons]="false"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [showPreviousNextButtons]="false"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [showEllipsis]="false"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [defaultItemsPerPage]="20"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [selectedPageBgColor]="'var(--orange)'"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [selectedPageTextColor]="'#ffffff'"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" [loading]="true"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" (pageChanged)="changePage($event)"></app-pagination-bar>
 * <app-pagination-bar [pagination]="pagination" (pageSizeChanged)="changePageSize($event)"></app-pagination-bar>
 *
 * NEW: <app-pagination-bar [isFrontendPagination]="true" [fullDataArray]="fullDataArray" [paginatedData]="paginatedData"></app-pagination-bar>
 */
export class PaginationBarComponent implements OnInit, OnChanges {
  /**
   * Whether to use frontend pagination or server-side pagination.
   * To be used only if frontend pagination is required and the full data array is provided.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [isFrontendPagination]="true"></app-pagination-bar>
   */
  @Input() isFrontendPagination = false;

  /**
   * The full data array to be paginated.
   * To be used only if isFrontendPagination is true.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [isFrontendPagination]="true" [fullDataArray]="fullDataArray"></app-pagination-bar>
   */
  @Input() fullDataArray: any[] = [];

  /**
   * The paginated data array to be emitted.
   * To be used only if isFrontendPagination is true and the full data array is provided.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [isFrontendPagination]="true" [fullDataArray]="fullDataArray" [paginatedData]="paginatedData"></app-pagination-bar>
   */
  @Output() paginatedData = new EventEmitter<any[]>();

  /**
   * The pagination object that contains the page, pageSize, rowCount and pageCount
   * properties.
   * @type {Pagination}
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [pagination]="pagination"></app-pagination-bar>
   */
  @Input() pagination: Pagination;

  /**
   * Whether to show the page size selector dropdown.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [showPageSizeSelector]="false"></app-pagination-bar>
   */
  @Input() showPageSizeSelector = true;

  /**
   * Whether to show the page size selector dropdown.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [showPageSizeSelector]="false"></app-pagination-bar>
   */
  @Input() showPagination = true;

  /**
   * The page size options to show in the page size selector dropdown.
   * @type {number[]}
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [itemsPerPageOptions]="[10, 20, 50, 100]"></app-pagination-bar>
   */
  @Input() itemsPerPageOptions: number[] = [10, 20, 50, 100];

  /**
   * Whether to show the first and last page buttons.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [showFirstLastButtons]="false"></app-pagination-bar>
   */
  @Input() showFirstLastButtons = false;

  /**
   * Whether to show the previous and next page buttons.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [showPreviousNextButtons]="false"></app-pagination-bar>
   */
  @Input() showPreviousNextButtons = true;

  /**
   * Whether to show the ellipsis (...) if page number exceeds 6.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [showEllipsis]="false"></app-pagination-bar>
   */
  @Input() showEllipsis = true;

  /**
   * The default page size to use if the pagination object does not have a pageSize property.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [defaultItemsPerPage]="20"></app-pagination-bar>
   */
  @Input() defaultItemsPerPage = this.itemsPerPageOptions[0];

  /**
   * The background color of the selected page buttons.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [pageBgColor]="'var(--orange)'"></app-pagination-bar>
   */
  @Input() selectedPageBgColor = 'var(--orange)';

  /**
   * The text color of the selected page buttons.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [pageTextColor]="'#ffffff'"></app-pagination-bar>
   */
  @Input() selectedPageTextColor = '#ffffff';

  /**
   * If true, the pagination bar will be disabled.
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar [loading]="true"></app-pagination-bar>
   */
  @Input() loading = false;

  /**
   * @deprecated - Use paginationUpdated event instead
   */
  @Output() pageChanged = new EventEmitter<number>();

  /**
   * @deprecated - Use paginationUpdated event instead
   */
  @Output() pageSizeChanged = new EventEmitter<number>();

  /**
   * Emits the updated pagination object when the page or page size is changed.
   * Emits Page and PageSize
   * @memberof PaginationBarComponent
   * @example
   * <app-pagination-bar (paginationUpdated)="paginationChange($event)"></app-pagination-bar>
   */
  @Output() paginationUpdated = new EventEmitter<Pagination>();

  isDropdownOpen = false;
  protected readonly isMobileView = isMobileView();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    // Check if the click is outside of the dropdown
    if (targetElement && !targetElement.closest('.mini-dropdown')) {
      this.isDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    if (this.pagination && !this.pagination.pageSize) {
      this.pagination.pageSize = this.defaultItemsPerPage;
    }
    // If it's frontend pagination, initialize the pagination on the full dataset
    if (this.isFrontendPagination) {
      this.pagination = this.pagination || {
        page: 1,
        pageSize: this.defaultItemsPerPage,
        rowCount: this.fullDataArray ? this.fullDataArray.length : 0,
        pageCount: this.fullDataArray ? Math.ceil(this.fullDataArray.length / this.defaultItemsPerPage) : 0,
      };
      this._paginateData();
    }

    if (this.itemsPerPageOptions && this.itemsPerPageOptions.length) {
      this.defaultItemsPerPage = this.itemsPerPageOptions[0];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Specifically target frontend pagination scenarios only
    if (this.isFrontendPagination) {
      if (changes['fullDataArray']) {
        // Adjust the pagination state based on the new fullDataArray length
        this._adjustPaginationForNewData();
      }

      if (changes['itemsPerPageOptions'] && this.itemsPerPageOptions.length) {
        // Adjust defaultItemsPerPage if the options change
        this._adjustDefaultItemsPerPage();
      }
    }
  }

  /**
   * The function "changePage" updates the current page number and emits an event if the new page is
   * valid.
   * @param {number} newPage - The newPage parameter is a number that represents the page number that we
   * want to change to.
   */
  changePage(newPage: number): void {
    if (newPage !== this.pagination.page && newPage > 0 && newPage <= this.pagination.pageCount) {
      this.pagination.page = newPage;
      // Emit page change as before
      this.pageChanged.emit(newPage);
      // Emit updated pagination object for comprehensive updates
      this.paginationUpdated.emit({ page: this.pagination.page, pageSize: this.pagination.pageSize });
      if (this.isFrontendPagination) {
        this._paginateData();
      }
    }
  }

  /**
   * The function "onPageSizeChange" updates the page size and resets the current page to the first page
   * when the page size is changed, and emits events to notify other components of the changes.
   * @param {number} newSize - The `newSize` parameter is a number that represents the new page size that
   * the user has selected.
   */
  onPageSizeChange(newSize: number): void {
    if (newSize !== this.pagination.pageSize) {
      this.pagination.page = 1;
      this.pagination.pageSize = newSize;
      // Emit page size change as before
      this.pageSizeChanged.emit(this.pagination.pageSize);
      if (this.isFrontendPagination) {
        this._paginateData();
      } else {
        // Update pageCount for server-side pagination
        this.pagination.pageCount = Math.ceil(this.pagination.rowCount / this.pagination.pageSize);
        // Since we are changing the page size, it's important to also consider this as a page change to the first page
        this.pageChanged.emit(1); // Keeping this to support existing behavior
      }
      // Emit updated pagination object for comprehensive updates
      this.paginationUpdated.emit({ page: this.pagination.page, pageSize: this.pagination.pageSize });
      this.isDropdownOpen = false;
    }
  }

  /**
   * The function `getPages` returns an array of page numbers or ellipses based on the current pagination
   * state.
   * @returns The function `getPages()` returns an array of numbers.
   */
  getPages(): (string | number)[] {
    const pageNumbers = [];
    let startPage: number, endPage: number;

    const maxPagesToShow = isMobileView() ? 3 : 6; // Determine the maximum number of buttons based on the view

    if (this.pagination.pageCount <= maxPagesToShow) {
      // less than maxPagesToShow total pages so show all
      startPage = 1;
      endPage = this.pagination.pageCount;
    } else {
      // more than maxPagesToShow total pages so calculate start and end pages
      if (this.pagination.page <= Math.ceil(maxPagesToShow / 2)) {
        startPage = 1;
        endPage = maxPagesToShow;
      } else if (this.pagination.page + Math.floor(maxPagesToShow / 2) >= this.pagination.pageCount) {
        startPage = this.pagination.pageCount - (maxPagesToShow - 1);
        endPage = this.pagination.pageCount;
      } else {
        startPage = this.pagination.page - Math.floor(maxPagesToShow / 2);
        endPage = this.pagination.page + Math.floor(maxPagesToShow / 2);
      }
    }

    // create an array of pages to ng-repeat in the pager control
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // add ellipses
    if (this.showEllipsis) {
      if (startPage > 1) {
        pageNumbers.unshift('...');
      }

      if (endPage < this.pagination.pageCount) {
        pageNumbers.push('...');
      }
    }

    return pageNumbers;
  }

  /**
   * The function toggleDropdown toggles the value of the isDropdownOpen variable.
   */
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  private _paginateData(): void {
    if (this.fullDataArray && this.pagination) {
      const start = (this.pagination.page - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      const paginatedItems = this.fullDataArray.slice(start, end);
      this.paginatedData.emit(paginatedItems);

      this.pagination.pageCount = Math.ceil(this.fullDataArray.length / this.pagination.pageSize);
      // Emit the updated pagination object here as well, to ensure any changes are reflected
      this.paginationUpdated.emit({ ...this.pagination });
    }
  }

  /**
   * The function `_adjustPaginationForNewData` adjusts the pagination state based on the new fullDataArray when it changes.
   * @private
   */
  private _adjustPaginationForNewData(): void {
    if (!this.fullDataArray || !this.pagination) return;
    this.pagination.rowCount = this.fullDataArray.length;
    this.pagination.pageCount = Math.ceil(this.fullDataArray.length / this.pagination.pageSize);
    this.pagination.page = 1; // Reset to the first page to reflect the new data
    this._paginateData(); // Update paginated data based on the new array
  }

  /**
   * The function `_adjustDefaultItemsPerPage` adjusts the defaultItemsPerPage if the itemsPerPageOptions change during frontend pagination.
   * @private
   */
  private _adjustDefaultItemsPerPage(): void {
    const previousDefaultItemsPerPage = this.defaultItemsPerPage;
    this.defaultItemsPerPage = this.itemsPerPageOptions[0];
    if (this.defaultItemsPerPage !== previousDefaultItemsPerPage) {
      this.pagination.pageSize = this.defaultItemsPerPage;
      this._paginateData(); // Re-trigger pagination with the new default page size
    }
  }
}
